'use client';
import { Avatar } from '@streetferret/ui-web/avatar';
import { Container } from '@streetferret/ui-web/container';
import { motion } from 'framer-motion';
import Image from 'next/image';
import PaulHeadshot from './_media/paul-headshot-small.png';

export function About() {
  return (
    <Container>
      <motion.section
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="py-6">
        <div className="grid gap-10 lg:grid-cols-[340px_1fr] lg:items-start">
          <div className="lg:itesms-start flex flex-col items-center">
            <Avatar className="!h-40 !w-40">
              <Image src={PaulHeadshot} alt="Paul Johnson" placeholder="blur" />
            </Avatar>

            <div className="mt-4 text-center">
              <h2 className="font-display text-3xl font-black leading-tight">
                Hi, I’m Paul.
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Naval Officer • Ultrarunner • Mental health advocate
              </p>
            </div>

            <blockquote className="mt-6 max-w-sm rounded-3xl border bg-muted/30 p-5 text-left">
              <p className="font-display text-xl font-black leading-snug">
                “Running was my therapy—my way back to myself.”
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Not a slogan. A practice. One day at a time.
              </p>
            </blockquote>
          </div>

          <div className="space-y-10">
            <section className="space-y-3">
              <h3 className="font-display text-2xl font-black">
                Before running: survival mode
              </h3>
              <p className="text-left leading-relaxed">
                In 2018, I commissioned as a Naval Officer and went
                forward-deployed to a ship homeported in Rota, Spain. The tour
                brought incredible opportunities—and a level of stress that
                quietly rewires you.
              </p>
              <p className="text-left leading-relaxed">
                Like a lot of people in high-pressure environments, I leaned on
                the wrong coping mechanisms. Alcohol was the default. And I
                didn’t fully see what it was costing until I stepped away from
                that constant pace.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="font-display text-2xl font-black">
                The turning point
              </h3>
              <p className="text-left leading-relaxed">
                In San Diego (2021), the symptoms got louder: sleep problems,
                nightmares, anxiety, depression. A Marine Officer friend asked
                me to train for the Marine Corps Marathon in 2022—and that
                invitation changed the story.
              </p>
              <p className="text-left leading-relaxed">
                Training gave me structure. Running gave me relief. I learned
                what it feels like when the body becomes a place you can live
                inside again.
              </p>
            </section>

            <section className="space-y-3">
              <h3 className="font-display text-2xl font-black">
                What I’m doing now
              </h3>
              <p className="text-left leading-relaxed">
                January 2, 2023, I started a run streak—running every day. It
                quickly pulled me into ultras and 100-mile races. But the bigger
                shift was internal: running became a positive outlet that
                strengthened my mental health instead of numbing it.
              </p>
              <p className="text-left leading-relaxed">
                As the journey grew, I committed to sharing it honestly—because
                the military and veteran communities carry a heavy, complicated
                load. Team RWB’s mission to enrich veterans’ lives through
                community and physical activity matches the work I want to do:
                help more people find movement, purpose, and each other.
              </p>
            </section>

            <section className="rounded-3xl border bg-muted/30 p-6">
              <h3 className="font-display text-2xl font-black">Milestones</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                The moments that shaped the work.
              </p>

              <div className="mt-6 space-y-7" style={{ counterReset: 'step' }}>
                {[
                  {
                    title: '2018 — Commissioned as a Naval Officer',
                    detail:
                      'Forward-deployed to a ship homeported in Rota, Spain.',
                  },
                  {
                    title: '2021 — San Diego',
                    detail:
                      'Stepping away from constant stress made the symptoms impossible to ignore.',
                  },
                  {
                    title: '2022 — Marathon training',
                    detail:
                      'A friend asked. I said yes. Running became a tool instead of an escape.',
                  },
                  {
                    title: '2023 — The run streak',
                    detail:
                      'Daily runs turned into ultras—and a healthier way to cope.',
                  },
                  {
                    title: 'Transcon — The big swing',
                    detail:
                      'A coast-to-coast project built to raise money and shine a light.',
                  },
                ].map(({ title, detail }) => (
                  <div key={title} className="step">
                    <div className="min-w-0">
                      <div className="font-semibold leading-snug">{title}</div>
                      <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {detail}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </motion.section>
    </Container>
  );
}
